import { getUsersMe, postAuthLogin, postAuthLogout, postAuthPassword, } from '@citylytics/core-api-ts-client/service';
import { redirect } from '@tanstack/react-router';
import Session from 'supertokens-web-js/recipe/session';
import { AUTH_PATH_PREFIX, LOGIN_PATH } from './session.constants.js';
import { validateLoginSearch } from './session.utils.js';
function readErrorMessage(params) {
    const { error, fallback } = params;
    if (error && typeof error === 'object' && 'message' in error) {
        return typeof error.message === 'string' ? error.message : fallback;
    }
    return fallback;
}
async function fetchCurrentUser() {
    const { data, error } = await getUsersMe({
        // In-memory only (via request interceptor Symbol): a 401 here means "not logged in".
        meta: { skipAuthRetry: true },
    });
    if (error || !data) {
        return null;
    }
    return data.data;
}
// The cookies are httpOnly, so whether a session exists is asked of Supertokens (which refreshes
// it if needed), and the user is fetched only then.
async function restoreSession(store) {
    if (!(await Session.doesSessionExist())) {
        store.getState().clearSession();
        return false;
    }
    const user = await fetchCurrentUser();
    if (!user) {
        store.getState().clearSession();
        return false;
    }
    store.getState().setUser(user);
    return true;
}
// Browsers read `\` as `/` and drop tabs and newlines, so `/\evil.com` looks like a path but
// leaves the site. Resolving against our own origin and comparing catches every such spelling.
/** Where to go after signing in: `redirectPath` when it stays on this site, else `/`. */
export function resolvePostLoginRedirect(redirectPath) {
    if (typeof redirectPath !== 'string' || !redirectPath.startsWith('/')) {
        return '/';
    }
    const origin = globalThis.location.origin;
    const resolved = new URL(redirectPath, origin);
    if (resolved.origin !== origin || resolved.pathname.startsWith(AUTH_PATH_PREFIX)) {
        return '/';
    }
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}
/**
 * Login, logout and the session checks every web app runs against core-api's `/auth` routes. A
 * factory because the in-flight restore is shared state: concurrent route guards await one
 * session check instead of each starting their own.
 */
export function createSessionService(params) {
    const { store } = params;
    let restoreSessionPromise = null;
    async function ensureAuthenticated() {
        if (store.getState().user !== null) {
            return true;
        }
        restoreSessionPromise ??= restoreSession(store).finally(() => {
            restoreSessionPromise = null;
        });
        return restoreSessionPromise;
    }
    /** A `beforeLoad` for signed-in routes: anyone without a session logs in, then comes back. */
    async function requireAuth({ location }) {
        if (!(await ensureAuthenticated())) {
            throw redirect({ to: LOGIN_PATH, search: { redirect: location.href } });
        }
    }
    /** Spread into the login route: its search params, and someone signed in goes straight on. */
    const loginRouteGuards = {
        validateSearch: validateLoginSearch,
        beforeLoad: async ({ search }) => {
            if (await ensureAuthenticated()) {
                throw redirect({ href: resolvePostLoginRedirect(search.redirect) });
            }
        },
        // The sign-in form stands alone over the video backdrop: there is no app around it to give it
        // a page's title and actions. `as const`, so the web apps' route-page lint reads the reason.
        staticData: {
            pageContract: { exempt: 'a signed-out form over the video backdrop' },
        },
    };
    /**
     * Reloads the signed-in user after something may have changed their own permissions (their
     * role, or their municipality's ceiling), so every gate reflects what the API now allows.
     * Throws when the reload fails: keeping the old user would leave the screen offering what the
     * API may now refuse, or hiding what it now allows, without anyone knowing.
     */
    async function refreshCurrentUser() {
        const user = await fetchCurrentUser();
        if (!user) {
            throw new Error('Could not reload the signed-in user after their permissions changed');
        }
        store.getState().setUser(user);
    }
    async function login(loginParams) {
        const { credentials, fallbackMessage } = loginParams;
        const { data, error } = await postAuthLogin({ body: credentials });
        if (error || !data) {
            throw new Error(readErrorMessage({ error, fallback: fallbackMessage }));
        }
        store.getState().setUser(data.data);
    }
    /**
     * Sets a login's password from a set-password link, then ends any session this browser had:
     * an invite opened while signed in as someone else would otherwise land the new user on the
     * other account, since the login page skips straight past a live session.
     */
    async function submitNewPassword(passwordParams) {
        const { body, fallbackMessage } = passwordParams;
        const { error } = await postAuthPassword({ body });
        if (error) {
            throw new Error(readErrorMessage({ error, fallback: fallbackMessage }));
        }
        if (await ensureAuthenticated()) {
            await logout();
        }
    }
    // Not Session.signOut(): that calls Supertokens' own sign-out route, which the backend switches
    // off so that /auth/logout is the one documented way to end a session.
    async function logout() {
        try {
            await postAuthLogout({ meta: { skipAuthRetry: true } });
        }
        finally {
            store.getState().clearSession();
        }
    }
    /**
     * The core-api client's `onSessionLost`: forget the user and go to the login page, coming back
     * here once signed in. Pages under `/auth/` are left alone, since their own 401s mean a wrong
     * password or a spent link, not a lost session.
     */
    function leaveLostSession(router) {
        store.getState().clearSession();
        const { pathname, href } = router.state.location;
        if (pathname.startsWith(AUTH_PATH_PREFIX)) {
            return;
        }
        void router.navigate({ to: LOGIN_PATH, search: { redirect: href }, replace: true });
    }
    return {
        ensureAuthenticated,
        requireAuth,
        loginRouteGuards,
        refreshCurrentUser,
        login,
        submitNewPassword,
        logout,
        leaveLostSession,
    };
}
